import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { editorStoreContext } from "../../../store/editorStore";
import Grid from "../../../components/FloorPlan/Grid";
import { FloorplannerStoreContext } from "../../../store/floorplannerStore";
import { renderStore } from "../../../store/renderStore";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import {
  SymbolType,
  WallType,
  isAreaFillSymbolType,
  isCircleStairsType,
  isRectStairsType,
  isSvgType,
} from "../../../types/floorplannerTypes";
import { consumeShouldSuppressClickAfterPanningDrag } from "../../../utils/panningGesture";
import AmbientOcclusionOverlay from "./AmbientOcclusionOverlay";
import { setSceneMaterialOpacity } from "./materialOpacity";
import FloorPlan3DCameraController from "./FloorPlan3DCameraController";
import FloorPlan3DRendererSettings from "./RendererSettings";
import {
  RoomCeilingLights,
  SunLight,
  getDaylightSettings,
  getRoomCeilingLights,
} from "./Lighting";
import { resolveFloorPlan3DScope } from "../scope/focusScope";
import Ceiling3DLayer from "../structures/ceilings/Ceiling3DLayer";
import Floor3DMeshes from "../structures/floors/Floor3DMeshes";
import {
  DEFAULT_FLOOR_THICKNESS,
  Floor3DShapeDefinition,
  createCeilingShapeDefinitionsFromFloors,
  createFloorShapeDefinitionsFromAreaFills,
  getHiddenHorizontalSurfaceIdsForCamera,
} from "../structures/floors/floorGeometry";
import Joint3DMeshes from "../structures/walls/Joint3DMeshes";
import Wall3DMeshes from "../structures/walls/Wall3DMeshes";
import WallAttachment3DMeshes from "../structures/walls/WallAttachment3DMeshes";
import {
  DEFAULT_WALL_HEIGHT,
  createWallEndpointHeightMap,
  getHiddenExteriorWallIdsForCamera,
  getPlanBoundsFromWalls,
  getWall3DHeight,
} from "../structures/walls/wallGeometry";
import { isStructure3DSymbol } from "../symbols/openings/openingGeometry";
import { useHydratePlacedProduct3DAssets } from "../products/useHydratePlacedProduct3DAssets";
import { getStandardSymbol3DAssetSignature } from "../symbols/assets/standardSymbol3DAssets";
import {
  applyFloorplannerSymbol3DDefaults,
  getFloorplannerSymbol3DDefaultKey,
  getSymbolsReadyForFloorplanner3DDefaults,
  useFloorplannerSymbol3DDefaults,
} from "../symbols/defaults/floorplannerSymbol3DDefaults";
import {
  BuiltInMaterialSurfaceMappingsContext,
  useFloorplannerBuiltInMaterialSurfaces,
} from "../materials/builtInMaterialSurfaceMappings";
import { getPrimitiveSymbol3DModelKeyForSymbol } from "../symbols/registry/primitiveSymbol3DConfig";
import LivingRoomPrimitive3DMeshes from "../symbols/primitives/livingRoom/LivingRoomPrimitive3DMeshes";
import ShapePrimitive3DMeshes from "../symbols/primitives/shapes/ShapePrimitive3DMeshes";
import { isShapePrimitiveSymbol } from "../symbols/primitives/shapes/shapePrimitiveGeometry";
import StairPrimitive3DMeshes from "../symbols/primitives/stairs/StairPrimitive3DMeshes";
import Measurement3DLayer from "../measurements/Measurement3DLayer";
import Ruler3DConstructionLayer from "../measurements/Ruler3DConstructionLayer";

const TRANSITION_MS = 260;
const MIN_DEPTH_SCALE = 0.001;
const GRID_BASE_PLANE_OFFSET = 0.01;
const POINTER_MISSED_DRAG_THRESHOLD_PX = 2;

type FloorPlan3DSceneProps = {
  active: boolean;
  entry2DView?: {
    x: number;
    y: number;
    z: number;
    zoom: number;
  } | null;
  onExit2DView?: (view: {
    x: number;
    y: number;
    z: number;
    zoom: number;
  }) => void;
  onExitComplete?: () => void;
  onModelLoadingChange?: (loading: boolean) => void;
  onFirstFrameReady?: () => void;
  renderMeasurements?: boolean;
};

const easeInOut = (t: number): number => 0.5 - Math.cos(Math.PI * t) / 2;

const FloorPlan3DScene: React.FC<FloorPlan3DSceneProps> = observer(
  ({
    active,
    entry2DView,
    onExit2DView,
    onExitComplete,
    onModelLoadingChange,
    onFirstFrameReady,
    renderMeasurements = true,
  }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const productAssetsEnabled = useFeatureFlag("floorplannerProduct3DAssets");
    const { camera, gl, invalidate } = useThree();
    const groupRef = useRef<THREE.Group | null>(null);
    const depthScaleRef = useRef(0);
    const opacityRef = useRef(0);
    const firstActiveFrameReportedRef = useRef(false);
    const firstFrameReadyRafRef = useRef<number | null>(null);
    const [openingAngleDragActive, setOpeningAngleDragActive] = useState(false);
    const [symbolDragActive, setSymbolDragActive] = useState(false);
    const [loadingSymbolAssetIds, setLoadingSymbolAssetIds] = useState<
      Set<string>
    >(new Set());
    const interactionLockRef = useRef({
      openingAngleDragActive: false,
      symbolDragActive: false,
    });
    const pointerMissedGestureRef = useRef<{
      pointerId: number;
      clientX: number;
      clientY: number;
      moved: boolean;
    } | null>(null);
    const lastPointerUpWasDragRef = useRef(false);
    const [cameraHiddenWallIds, setCameraHiddenWallIds] = useState<Set<string>>(
      new Set(),
    );
    const cameraHiddenWallIdsRef = useRef<Set<string>>(new Set());
    const [cameraHiddenFloorIds, setCameraHiddenFloorIds] = useState<
      Set<string>
    >(new Set());
    const cameraHiddenFloorIdsRef = useRef<Set<string>>(new Set());
    const [cameraHiddenCeilingIds, setCameraHiddenCeilingIds] = useState<
      Set<string>
    >(new Set());
    const cameraHiddenCeilingIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
      firstActiveFrameReportedRef.current = false;
      if (firstFrameReadyRafRef.current !== null) {
        cancelAnimationFrame(firstFrameReadyRafRef.current);
        firstFrameReadyRafRef.current = null;
      }
    }, [active]);

    useEffect(
      () => () => {
        if (firstFrameReadyRafRef.current !== null) {
          cancelAnimationFrame(firstFrameReadyRafRef.current);
        }
      },
      [],
    );

    useFrame(() => {
      if (!active || firstActiveFrameReportedRef.current) return;

      firstActiveFrameReportedRef.current = true;
      firstFrameReadyRafRef.current = requestAnimationFrame(() => {
        firstFrameReadyRafRef.current = null;
        onFirstFrameReady?.();
      });
    });

    const scopeData = resolveFloorPlan3DScope(floorplannerStore, {
      kind: "canvas",
    });
    const { walls, rooms, symbols, rulerLines, rulers3D } = scopeData;
    useHydratePlacedProduct3DAssets({
      enabled: active && productAssetsEnabled,
      symbols,
      store: floorplannerStore,
    });
    const floorplannerSymbolDefaultKeys = useMemo(
      () =>
        Array.from(
          new Set(
            symbols
              .map(getFloorplannerSymbol3DDefaultKey)
              .filter((key): key is string => Boolean(key)),
          ),
        ).sort(),
      [symbols],
    );
    const {
      defaultsByKey: floorplannerSymbolDefaultsByKey,
      defaultProductAssetsByProductId,
      loading: floorplannerSymbolDefaultsLoading,
      signature: floorplannerSymbolDefaultsSignature,
    } = useFloorplannerSymbol3DDefaults(floorplannerSymbolDefaultKeys, active);
    const { signature: builtInMaterialSurfaceMappingsSignature } =
      useFloorplannerBuiltInMaterialSurfaces(active);
    const effectiveSymbols = useMemo(
      () =>
        applyFloorplannerSymbol3DDefaults(
          symbols,
          floorplannerSymbolDefaultsByKey,
          defaultProductAssetsByProductId,
        ),
      [
        defaultProductAssetsByProductId,
        floorplannerSymbolDefaultsByKey,
        floorplannerSymbolDefaultsSignature,
        symbols,
      ],
    );
    const floorplannerSymbolDefaultsQueryPending = Boolean(
      active &&
        floorplannerSymbolDefaultsLoading &&
        floorplannerSymbolDefaultKeys.length > 0,
    );
    const renderableEffectiveSymbols = useMemo(
      () =>
        getSymbolsReadyForFloorplanner3DDefaults(
          effectiveSymbols,
          floorplannerSymbolDefaultsQueryPending,
        ),
      [effectiveSymbols, floorplannerSymbolDefaultsQueryPending],
    );
    const floorplannerSymbolDefaultsPending = Boolean(
      floorplannerSymbolDefaultsQueryPending &&
        renderableEffectiveSymbols.length < effectiveSymbols.length,
    );
    const handleSymbolAssetLoadingChange = useCallback(
      (symbolId: string, loading: boolean) => {
        setLoadingSymbolAssetIds((current) => {
          if (current.has(symbolId) === loading) return current;
          const next = new Set(current);
          if (loading) {
            next.add(symbolId);
          } else {
            next.delete(symbolId);
          }
          return next;
        });
      },
      [],
    );
    const modelLoading = Boolean(
      active &&
        (floorplannerSymbolDefaultsPending || loadingSymbolAssetIds.size > 0),
    );
    useEffect(() => {
      onModelLoadingChange?.(modelLoading);
    }, [modelLoading, onModelLoadingChange]);
    useEffect(
      () => () => {
        onModelLoadingChange?.(false);
      },
      [onModelLoadingChange],
    );
    const standardSymbolAssetSignature = getStandardSymbol3DAssetSignature();
    const selectedIds = new Set(
      editorStore.selections.map((selection) => selection.id),
    );
    const singleSelectedId =
      editorStore.selections.length === 1 ? editorStore.selections[0].id : null;
    const selectedSymbolRotationGizmoVisible = Boolean(
      !editorStore.panning &&
        !editorStore.ruler3DConstructionMode &&
        singleSelectedId &&
        renderableEffectiveSymbols.some(
          (symbol) =>
            symbol.id === singleSelectedId &&
            supportsAutomaticSymbolRotationGizmo(symbol, productAssetsEnabled),
        ),
    );
    const wallsById = new Map(walls.map((wall) => [wall.id, wall]));
    const jointPolygons = Array.from(renderStore.jointPolygonsMap.values());
    const wallSignature = walls
      .map((wall) =>
        [
          wall.id,
          wall.geometryVersion ?? 0,
          wall.lastUpdate ?? 0,
          wall.start.x,
          wall.start.y,
          wall.end.x,
          wall.end.y,
          wall.controlPoint?.x ?? "",
          wall.controlPoint?.y ?? "",
          wall.wallWidth ?? "",
          wall.threeD?.height ?? "",
          // Ready / catalog materials must invalidate 3D wall meshes on change.
          wall.threeD?.materialId ?? "",
          wall.threeD?.surfaceRef?.id ?? "",
          wall.threeD?.surfaceRef?.imageUrl ?? "",
          wall.threeD?.surfaceRef?.color ?? "",
          wall.symbolAttachments
            ?.map(
              (attachment) =>
                `${attachment.symbolId}:${attachment.positionFromStart}`,
            )
            .join(",") ?? "",
        ].join(":"),
      )
      .join("|");
    const openingGeometrySignature = walls
      .map((wall) =>
        [
          wall.id,
          wall.geometryVersion ?? 0,
          wall.lastUpdate ?? 0,
          wall.start.x,
          wall.start.y,
          wall.end.x,
          wall.end.y,
          wall.controlPoint?.x ?? "",
          wall.controlPoint?.y ?? "",
          wall.wallWidth ?? "",
          wall.threeD?.height ?? "",
          wall.symbolAttachments
            ?.map((attachment) => {
              const symbol = floorplannerStore.symbolsMap.get(
                attachment.symbolId,
              );
              return [
                attachment.symbolId,
                attachment.positionFromStart,
                symbol?.type ?? "",
                symbol && "doorWidth" in symbol ? symbol.doorWidth : "",
                symbol && "doubleDoorWidth" in symbol
                  ? symbol.doubleDoorWidth
                  : "",
                symbol && "windowLength" in symbol ? symbol.windowLength : "",
                symbol && "voidLength" in symbol ? symbol.voidLength : "",
                symbol && "doorFrameWidth" in symbol
                  ? (symbol.doorFrameWidth ?? "")
                  : "",
                symbol && "windowFrameWidth" in symbol
                  ? (symbol.windowFrameWidth ?? "")
                  : "",
                symbol?.threeD?.height ?? "",
                symbol?.threeD?.baseElevation ?? "",
                symbol?.threeD?.topBulge ?? "",
              ].join("=");
            })
            .join(",") ?? "",
        ].join(":"),
      )
      .join("|");
    const symbolVisualSignature = symbols
      .map((symbol) =>
        [
          symbol.id,
          symbol.type,
          symbol.libraryKey ?? "",
          symbol.threeD?.modelKey ?? "",
          symbol.threeD?.sourceMode ?? "",
          symbol.threeD?.fillColorOverride ? "fillOverride" : "",
          symbol.threeD?.userModel?.modelId ?? "",
          symbol.threeD?.userModel?.status ?? "",
          symbol.threeD?.userModel?.url ?? "",
          symbol.threeD?.userModel?.sha256 ?? "",
          symbol.threeD?.userModel?.version ?? "",
          symbol.threeD?.userModel?.pitchOffset ?? "",
          symbol.threeD?.userModel?.rollOffset ?? "",
          symbol.threeD?.userModel?.yawOffset ?? "",
          symbol.threeD?.modelPitchOffset ?? "",
          symbol.threeD?.modelRollOffset ?? "",
          symbol.threeD?.modelYawOffset ?? "",
          symbol.threeD?.userModel?.dimensions?.width ?? "",
          symbol.threeD?.userModel?.dimensions?.height ?? "",
          symbol.threeD?.userModel?.dimensions?.depth ?? "",
          symbol.position.x,
          symbol.position.y,
          symbol.rotation ?? "",
          isSvgType(symbol) ? (symbol.flipped ?? "legacy") : "",
          symbol.flipHorizontal ? "flipX" : "",
          symbol.flipVertical ? "flipY" : "",
          symbol.attachedTo ?? "",
          symbol.threeD?.height ?? "",
          symbol.threeD?.baseElevation ?? "",
          symbol.threeD?.topBulge ?? "",
          symbol.threeD?.shapeKind ?? "",
          symbol.threeD?.placementMode ?? "",
          symbol.threeD?.rotation3D?.pitch ?? "",
          symbol.threeD?.rotation3D?.yaw ?? "",
          symbol.threeD?.rotation3D?.roll ?? "",
          symbol.threeD?.wallMount?.wallId ?? "",
          symbol.threeD?.wallMount?.side ?? "",
          symbol.threeD?.wallMount?.offset ?? "",
          symbol.threeD?.wallMount?.distanceFromWall ?? "",
          symbol.threeD?.dimensions?.width ?? "",
          symbol.threeD?.dimensions?.height ?? "",
          symbol.threeD?.dimensions?.depth ?? "",
          "svgLength" in symbol ? (symbol.svgLength ?? "") : "",
          "svgHeight" in symbol ? (symbol.svgHeight ?? "") : "",
          "openAngle" in symbol ? (symbol.openAngle ?? "") : "",
          "polygonKind" in symbol ? (symbol.polygonKind ?? "") : "",
          "polygonWidth" in symbol ? (symbol.polygonWidth ?? "") : "",
          "polygonHeight" in symbol ? (symbol.polygonHeight ?? "") : "",
          "circleStairsWidth" in symbol ? (symbol.circleStairsWidth ?? "") : "",
          "rectStairsWidth" in symbol ? (symbol.rectStairsWidth ?? "") : "",
          "rectStairsHeight" in symbol ? (symbol.rectStairsHeight ?? "") : "",
          "stairStepSize" in symbol ? (symbol.stairStepSize ?? "") : "",
          symbol.fillColor ?? "",
          symbol.fillOpacity ?? "",
          symbol.fillPattern ?? "",
          symbol.threeD?.surfaceRef?.id ?? "",
          symbol.threeD?.surfaceRef?.imageUrl ?? "",
          Object.entries(symbol.threeD?.materialAssignments ?? {})
            .sort(([left], [right]) => left.localeCompare(right))
            .map(
              ([slot, assignment]) =>
                `${slot}=${assignment.materialId}:${assignment.surfaceRef?.id ?? ""}:${assignment.surfaceRef?.imageUrl ?? ""}`,
            )
            .join(","),
          symbol.threeD?.product?.productId ?? "",
          symbol.threeD?.product?.variantId ?? "",
          symbol.threeD?.product?.renderMode ?? "",
          symbol.threeD?.product?.proxyModelKey ?? "",
          symbol.threeD?.product?.selectedImageUrl ?? "",
          symbol.threeD?.product?.asset?.id ?? "",
          symbol.threeD?.product?.asset?.status ?? "",
          symbol.threeD?.product?.asset?.glbUrl ?? "",
          symbol.threeD?.product?.asset?.thumbnailUrl ?? "",
          symbol.threeD?.product?.asset?.quality?.sha256 ?? "",
          symbol.threeD?.product?.asset?.quality?.pipelineVersion ?? "",
          symbol.threeD?.product?.asset?.dimensions?.width ?? "",
          symbol.threeD?.product?.asset?.dimensions?.height ?? "",
          symbol.threeD?.product?.asset?.dimensions?.depth ?? "",
          symbol.threeD?.product?.dimensions?.width ?? "",
          symbol.threeD?.product?.dimensions?.height ?? "",
          symbol.threeD?.product?.dimensions?.depth ?? "",
        ].join(":"),
      )
      .join("|");
    const areaFillSignature = symbols
      .filter(isAreaFillSymbolType)
      .map((symbol) =>
        [
          symbol.id,
          symbol.position.x,
          symbol.position.y,
          symbol.fillColor ?? "",
          symbol.fillOpacity ?? "",
          symbol.fillPattern ?? "",
          symbol.threeD?.surfaceRef?.id ?? "",
          symbol.threeD?.surfaceRef?.imageUrl ?? "",
          symbol.roomBinding?.roomId ?? "",
          symbol.roomBinding?.roomSignature ?? "",
          symbol.roomBinding?.objectIds.join(",") ?? "",
          symbol.vertices.map((vertex) => `${vertex.x},${vertex.y}`).join(";"),
        ].join(":"),
      )
      .join("|");
    const roomSignature = rooms
      .map((room) =>
        [
          room.id,
          room.objectIds.join(","),
          room.centroid.x,
          room.centroid.y,
          room.fillColor ?? "",
        ].join(":"),
      )
      .join("|");
    const planBounds = useMemo(
      () => getPlanBoundsFromWalls(walls),
      [wallSignature],
    );
    const cameraVisibilityTarget = useMemo(() => {
      const center = planBounds.isEmpty()
        ? new THREE.Vector2(0, 0)
        : planBounds.getCenter(new THREE.Vector2());
      return new THREE.Vector3(center.x, center.y, 0.85);
    }, [planBounds]);
    const cameraHiddenWallSignature = useMemo(
      () => Array.from(cameraHiddenWallIds).sort().join(","),
      [cameraHiddenWallIds],
    );
    const cameraHiddenFloorSignature = useMemo(
      () => Array.from(cameraHiddenFloorIds).sort().join(","),
      [cameraHiddenFloorIds],
    );
    const cameraHiddenCeilingSignature = useMemo(
      () => Array.from(cameraHiddenCeilingIds).sort().join(","),
      [cameraHiddenCeilingIds],
    );

    useEffect(() => {
      interactionLockRef.current = {
        openingAngleDragActive,
        symbolDragActive,
      };
    }, [openingAngleDragActive, symbolDragActive]);

    useEffect(() => {
      if (!active) return;

      const releaseInteractionLock = () => {
        const interactionLock = interactionLockRef.current;
        if (
          !interactionLock.openingAngleDragActive &&
          !interactionLock.symbolDragActive
        ) {
          return;
        }

        interactionLockRef.current = {
          openingAngleDragActive: false,
          symbolDragActive: false,
        };
        if (interactionLock.openingAngleDragActive) {
          setOpeningAngleDragActive(false);
        }
        if (interactionLock.symbolDragActive) {
          setSymbolDragActive(false);
        }
        invalidate();
      };

      const releaseStaleInteractionLock = () => {
        window.setTimeout(releaseInteractionLock, 0);
      };

      window.addEventListener("pointerup", releaseStaleInteractionLock, true);
      window.addEventListener(
        "pointercancel",
        releaseStaleInteractionLock,
        true,
      );
      window.addEventListener("blur", releaseInteractionLock);
      document.addEventListener("visibilitychange", releaseInteractionLock);

      return () => {
        window.removeEventListener(
          "pointerup",
          releaseStaleInteractionLock,
          true,
        );
        window.removeEventListener(
          "pointercancel",
          releaseStaleInteractionLock,
          true,
        );
        window.removeEventListener("blur", releaseInteractionLock);
        document.removeEventListener(
          "visibilitychange",
          releaseInteractionLock,
        );
      };
    }, [active, invalidate]);

    useEffect(() => {
      if (!active) return;

      const handlePointerDown = (event: PointerEvent) => {
        if (event.button !== 0) return;
        lastPointerUpWasDragRef.current = false;
        pointerMissedGestureRef.current = {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
          moved: false,
        };
      };
      const handlePointerMove = (event: PointerEvent) => {
        const gesture = pointerMissedGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        if (hasPointerMissedGestureMoved(gesture, event)) {
          gesture.moved = true;
        }
      };
      const handlePointerUp = (event: PointerEvent) => {
        const gesture = pointerMissedGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        lastPointerUpWasDragRef.current =
          gesture.moved || hasPointerMissedGestureMoved(gesture, event);
        pointerMissedGestureRef.current = null;
      };
      const handlePointerCancel = (event: PointerEvent) => {
        const gesture = pointerMissedGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        pointerMissedGestureRef.current = null;
        lastPointerUpWasDragRef.current = false;
      };

      const element = gl.domElement;
      element.addEventListener("pointerdown", handlePointerDown, true);
      element.addEventListener("pointermove", handlePointerMove, true);
      element.addEventListener("pointerup", handlePointerUp, true);
      element.addEventListener("pointercancel", handlePointerCancel, true);

      return () => {
        element.removeEventListener("pointerdown", handlePointerDown, true);
        element.removeEventListener("pointermove", handlePointerMove, true);
        element.removeEventListener("pointerup", handlePointerUp, true);
        element.removeEventListener("pointercancel", handlePointerCancel, true);
      };
    }, [active, gl.domElement]);

    useEffect(() => {
      if (!active) return;

      renderStore.composeAllShapes();
      walls.forEach((wall) => {
        renderStore.generateJointPolygons(wall.id);
        wall.symbolAttachments?.forEach((attachment) => {
          const symbol = floorplannerStore.symbolsMap.get(attachment.symbolId);
          if (!symbol || !isStructure3DSymbol(symbol)) return;
          renderStore.addWallClipping(
            wall.id,
            renderStore.getClippingPolygon(symbol, wall),
          );
        });
      });
      gl.shadowMap.needsUpdate = true;
      invalidate();
    }, [active, gl, invalidate, openingGeometrySignature]);

    useEffect(() => {
      let animationFrame = 0;
      const from = depthScaleRef.current;
      const to = active ? 1 : 0;
      const opacityFrom = opacityRef.current;
      const start = performance.now();

      const tick = () => {
        const progress = Math.min(
          (performance.now() - start) / TRANSITION_MS,
          1,
        );
        const eased = easeInOut(progress);
        const nextScale = from + (to - from) * eased;
        const nextOpacity = opacityFrom + (to - opacityFrom) * eased;
        depthScaleRef.current = nextScale;
        opacityRef.current = nextOpacity;
        groupRef.current?.scale.set(1, 1, Math.max(nextScale, MIN_DEPTH_SCALE));
        if (groupRef.current) {
          setSceneMaterialOpacity(groupRef.current, nextOpacity);
        }
        gl.shadowMap.needsUpdate = true;
        invalidate();

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        } else {
          if (active && groupRef.current) {
            setSceneMaterialOpacity(groupRef.current, 1);
          } else if (!active) {
            onExitComplete?.();
          }
        }
      };

      animationFrame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(animationFrame);
    }, [active, gl, invalidate, onExitComplete]);

    useLayoutEffect(() => {
      if (!groupRef.current) return;
      groupRef.current.scale.set(
        1,
        1,
        Math.max(depthScaleRef.current, MIN_DEPTH_SCALE),
      );
      setSceneMaterialOpacity(groupRef.current, opacityRef.current);
    });

    const roomSurfaceVerticesBySignature = useMemo(() => {
      const map = new Map<string, THREE.Vector2[]>();
      rooms.forEach((room) => {
        const signature = room.objectIds.slice().sort().join(",");
        if (!signature) return;
        map.set(
          signature,
          floorplannerStore
            .getRoomRenderFillVertices(room)
            .map((vertex) => vertex.clone()),
        );
      });
      return map;
    }, [floorplannerStore, roomSignature, wallSignature]);

    /**
     * 3D floors follow 2D area fills only. Do not invent a convex-hull
     * floor from walls when there is no room fill — open wall chains
     * (e.g. a Z-shape) would otherwise get a spurious slab between them.
     */
    const floors = useMemo<Floor3DShapeDefinition[]>(
      () =>
        createFloorShapeDefinitionsFromAreaFills(symbols, {
          roomSurfaceVerticesBySignature,
        }),
      [areaFillSignature, roomSurfaceVerticesBySignature],
    );
    const wallHeightById = useMemo(() => {
      const map = new Map<string, number>();
      walls.forEach((wall) => map.set(wall.id, getWall3DHeight(wall)));
      return map;
    }, [wallSignature]);
    const wallEndpointHeightMap = useMemo(
      () => createWallEndpointHeightMap(walls),
      [wallSignature],
    );
    const ceilings = useMemo(
      () =>
        createCeilingShapeDefinitionsFromFloors(floors, rooms, wallHeightById),
      [floors, roomSignature, wallHeightById],
    );
    const floorOcclusionSurfaces = useMemo(
      () =>
        floors.map((floor) => ({
          id: floor.id,
          shape: floor.shape,
          z: 0,
          hideWhenCameraIs: "below" as const,
        })),
      [floors],
    );
    const ceilingOcclusionSurfaces = useMemo(
      () =>
        ceilings.map((ceiling) => ({
          id: ceiling.id,
          shape: ceiling.shape,
          z: ceiling.height,
          hideWhenCameraIs: "above" as const,
        })),
      [ceilings],
    );

    useFrame(() => {
      if (!active) return;

      const cameraDirection = camera.getWorldDirection(new THREE.Vector3());
      const nextHiddenWallIds = getHiddenExteriorWallIdsForCamera(
        walls,
        camera.position,
        cameraVisibilityTarget,
        rooms,
        cameraDirection,
      );
      const nextHiddenFloorIds = getHiddenHorizontalSurfaceIdsForCamera(
        floorOcclusionSurfaces,
        camera.position,
        cameraDirection,
      );
      // "Show ceilings" keeps solid meshes so color/preview is visible from above.
      const nextHiddenCeilingIds = editorStore.showCeilings
        ? new Set<string>()
        : getHiddenHorizontalSurfaceIdsForCamera(
            ceilingOcclusionSurfaces,
            camera.position,
            cameraDirection,
          );

      const wallsChanged = !setsEqual(
        cameraHiddenWallIdsRef.current,
        nextHiddenWallIds,
      );
      const floorsChanged = !setsEqual(
        cameraHiddenFloorIdsRef.current,
        nextHiddenFloorIds,
      );
      const ceilingsChanged = !setsEqual(
        cameraHiddenCeilingIdsRef.current,
        nextHiddenCeilingIds,
      );
      if (!wallsChanged && !floorsChanged && !ceilingsChanged) return;

      if (wallsChanged) {
        cameraHiddenWallIdsRef.current = nextHiddenWallIds;
        setCameraHiddenWallIds(nextHiddenWallIds);
      }
      if (floorsChanged) {
        cameraHiddenFloorIdsRef.current = nextHiddenFloorIds;
        setCameraHiddenFloorIds(nextHiddenFloorIds);
      }
      if (ceilingsChanged) {
        cameraHiddenCeilingIdsRef.current = nextHiddenCeilingIds;
        setCameraHiddenCeilingIds(nextHiddenCeilingIds);
      }

      gl.shadowMap.needsUpdate = true;
      invalidate();
    });

    useEffect(() => {
      if (!active) {
        const emptyHiddenWallIds = new Set<string>();
        const emptyHiddenFloorIds = new Set<string>();
        const emptyHiddenCeilingIds = new Set<string>();
        cameraHiddenWallIdsRef.current = emptyHiddenWallIds;
        cameraHiddenFloorIdsRef.current = emptyHiddenFloorIds;
        cameraHiddenCeilingIdsRef.current = emptyHiddenCeilingIds;
        setCameraHiddenWallIds(emptyHiddenWallIds);
        setCameraHiddenFloorIds(emptyHiddenFloorIds);
        setCameraHiddenCeilingIds(emptyHiddenCeilingIds);
      }
    }, [active]);

    const daylightSettings = useMemo(
      () =>
        getDaylightSettings(
          planBounds,
          floorplannerStore.lighting.sunEnabled,
          floorplannerStore.lighting.sunTimeOfDay,
        ),
      [
        floorplannerStore.lighting.sunEnabled,
        floorplannerStore.lighting.sunTimeOfDay,
        planBounds,
      ],
    );
    const roomCeilingLights = useMemo(
      () =>
        getRoomCeilingLights(
          floors,
          rooms,
          wallHeightById,
          planBounds,
        ),
      [floors, planBounds, roomSignature, wallHeightById],
    );
    const ambientOcclusionWallHeight = useMemo(
      () =>
        Math.max(DEFAULT_WALL_HEIGHT, ...Array.from(wallHeightById.values())),
      [wallHeightById],
    );
    const shadowUpdateKey = [
      wallSignature,
      roomSignature,
      areaFillSignature,
      symbolVisualSignature,
      floorplannerSymbolDefaultsSignature,
      standardSymbolAssetSignature,
      cameraHiddenWallSignature,
      cameraHiddenFloorSignature,
      cameraHiddenCeilingSignature,
      floorplannerStore.lighting.sunEnabled,
      floorplannerStore.lighting.sunTimeOfDay,
      floorplannerStore.lighting.roomLightsEnabled,
      editorStore.hide3DShadows ? "0" : "1",
    ].join("|");

    useEffect(() => {
      if (!active || editorStore.selections.length > 0) return;
      if (!openingAngleDragActive && !symbolDragActive) return;

      setOpeningAngleDragActive(false);
      setSymbolDragActive(false);
      invalidate();
    }, [
      active,
      editorStore.selections.length,
      invalidate,
      openingAngleDragActive,
      symbolDragActive,
    ]);

    const clearWorkspaceSelection = useCallback(
      (event?: ThreeEvent<PointerEvent>) => {
        if (!active) return;
        if (editorStore.ruler3DConstructionMode) return;
        event?.stopPropagation();
        setOpeningAngleDragActive(false);
        setSymbolDragActive(false);
        editorStore.clearSelections();
        invalidate();
      },
      [active, editorStore, invalidate],
    );

    const handlePointerMissed = useCallback(() => {
      if (!active) return;
      if (lastPointerUpWasDragRef.current) return;
      clearWorkspaceSelection();
    }, [active, clearWorkspaceSelection]);

    const selectWall = useCallback(
      (wall: WallType, event: ThreeEvent<PointerEvent>) => {
        if (editorStore.ruler3DConstructionMode) return;
        // Locked viewers stay read-only. 2D holds them in pan mode, but 3D keeps
        // panning off so they can orbit — without this, a selection here would
        // let them drag objects into edits that can never be saved.
        if (floorplannerStore.lockedByOther) return;
        event.stopPropagation();
        if (
          !editorStore.isShiftPressed &&
          !editorStore.selections.some((selection) => selection.id === wall.id)
        ) {
          editorStore.clearSelections();
        }
        floorplannerStore.selectWall(wall.id);
        editorStore.addSelection(wall);
        invalidate();
      },
      [editorStore, floorplannerStore, invalidate],
    );

    const selectSymbol = useCallback(
      (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => {
        if (editorStore.ruler3DConstructionMode) return;
        if (floorplannerStore.lockedByOther) return;
        event.stopPropagation();
        if (
          !editorStore.isShiftPressed &&
          !editorStore.selections.some(
            (selection) => selection.id === symbol.id,
          )
        ) {
          editorStore.clearSelections();
        }
        floorplannerStore.selectSymbol(symbol.id);
        editorStore.addSelection(symbol);
        invalidate();
      },
      [editorStore, floorplannerStore, invalidate],
    );

    const selectAreaFillFloor = useCallback(
      (floor: Floor3DShapeDefinition, event?: ThreeEvent<PointerEvent>) => {
        if (editorStore.ruler3DConstructionMode) return;
        if (floorplannerStore.lockedByOther) return;
        event?.stopPropagation();

        if (
          editorStore.panning &&
          consumeShouldSuppressClickAfterPanningDrag()
        ) {
          return;
        }

        const symbol = floorplannerStore.symbolsMap.get(floor.id);
        if (!isAreaFillSymbolType(symbol)) return;

        if (
          !editorStore.isShiftPressed &&
          !editorStore.selections.some(
            (selection) => selection.id === symbol.id,
          )
        ) {
          editorStore.clearSelections();
        }
        floorplannerStore.selectSymbol(symbol.id);
        editorStore.addSelection(symbol);
        invalidate();
      },
      [editorStore, floorplannerStore, invalidate],
    );

    return (
      <BuiltInMaterialSurfaceMappingsContext.Provider
        value={builtInMaterialSurfaceMappingsSignature}
      >
        <FloorPlan3DRendererSettings
          active={active}
          shadowsEnabled={!editorStore.hide3DShadows}
          shadowUpdateKey={shadowUpdateKey}
        />
        <FloorPlan3DCameraController
          active={active}
          planBounds={planBounds}
          controlsEnabled={
            !openingAngleDragActive &&
            !symbolDragActive &&
            !editorStore.ruler3DConstructionMode
          }
          suppressBasePanHitTarget={selectedSymbolRotationGizmoVisible}
          entry2DView={entry2DView}
          onExit2DView={onExit2DView}
          onExitComplete={onExitComplete}
        />
        <hemisphereLight
          args={[0xf8f1e7, 0x6d665e, daylightSettings.skyIntensity]}
        />
        <ambientLight intensity={daylightSettings.ambientIntensity} />
        {floorplannerStore.lighting.sunEnabled &&
          daylightSettings.intensity > 0.01 && (
            <SunLight
              settings={daylightSettings}
              castShadow={!editorStore.hide3DShadows}
            />
          )}
        {floorplannerStore.lighting.roomLightsEnabled && (
          <RoomCeilingLights lights={roomCeilingLights} />
        )}
        {editorStore.showGrid && (
          <Grid
            positionZ={-DEFAULT_FLOOR_THICKNESS - GRID_BASE_PLANE_OFFSET}
            opacity={1}
            depthWrite
          />
        )}
        <group
          ref={(group) => {
            groupRef.current = group;
            if (group) {
              group.scale.set(
                1,
                1,
                Math.max(depthScaleRef.current, MIN_DEPTH_SCALE),
              );
              setSceneMaterialOpacity(group, opacityRef.current);
            }
          }}
          onPointerMissed={handlePointerMissed}
        >
          <Floor3DMeshes
            floors={floors}
            selectedIds={selectedIds}
            hiddenFloorIds={cameraHiddenFloorIds}
            panning={editorStore.panning}
            onSelectAreaFill={selectAreaFillFloor}
            onSelectWorkspace={clearWorkspaceSelection}
          />
          <Ceiling3DLayer
            ceilings={ceilings}
            walls={walls}
            wallEndpointHeightMap={wallEndpointHeightMap}
            hiddenCeilingIds={cameraHiddenCeilingIds}
          />

          {walls.map((wall) => {
            // Session-only user hide: omit entirely (no camera occluder edges).
            if (editorStore.isWallHidden(wall.id)) return null;
            return (
              <Wall3DMeshes
                key={wall.id}
                wall={wall}
                heightProfile={wallEndpointHeightMap.get(wall.id)}
                selected={selectedIds.has(wall.id)}
                cameraHidden={cameraHiddenWallIds.has(wall.id)}
                onSelect={selectWall}
              />
            );
          })}

          <Joint3DMeshes
            jointPolygons={jointPolygons}
            wallsById={wallsById}
            wallHeightById={wallHeightById}
            wallEndpointHeightMap={wallEndpointHeightMap}
            cameraHiddenWallIds={cameraHiddenWallIds}
            userHiddenWallIds={editorStore.hiddenWallIds}
          />

          {walls.map((wall) => {
            if (editorStore.isWallHidden(wall.id)) return null;
            return (
              <WallAttachment3DMeshes
                key={`attachments-${wall.id}`}
                wall={wall}
                symbols={symbols}
                wallHeight={wallHeightById.get(wall.id) ?? DEFAULT_WALL_HEIGHT}
                wallEndpointHeights={wallEndpointHeightMap.get(wall.id)}
                selected={selectedIds.has(wall.id)}
                selectedSymbolIds={selectedIds}
                cameraHidden={cameraHiddenWallIds.has(wall.id)}
                onSelectWall={selectWall}
                onSelectSymbol={selectSymbol}
                onOpeningAngleDragActiveChange={setOpeningAngleDragActive}
              />
            );
          })}

          <StairPrimitive3DMeshes
            symbols={symbols}
            walls={walls}
            selectedSymbolIds={selectedIds}
            onSelectSymbol={selectSymbol}
            onDragActiveChange={setSymbolDragActive}
          />
          <LivingRoomPrimitive3DMeshes
            symbols={renderableEffectiveSymbols}
            walls={walls}
            selectedSymbolIds={selectedIds}
            assetRenderingEnabled={productAssetsEnabled}
            onSelectSymbol={selectSymbol}
            onDragActiveChange={setSymbolDragActive}
            onAssetLoadingChange={handleSymbolAssetLoadingChange}
            persistThreeDOnDrag={false}
          />
          <ShapePrimitive3DMeshes
            symbols={symbols.filter((symbol) => {
              const mountWallId = symbol.threeD?.wallMount?.wallId;
              return !mountWallId || !editorStore.isWallHidden(mountWallId);
            })}
            walls={walls}
            selectedSymbolIds={selectedIds}
            onSelectSymbol={selectSymbol}
            onDragActiveChange={setSymbolDragActive}
          />
          {renderMeasurements && (
            <Measurement3DLayer
              walls={walls}
              rulerLines={rulerLines}
              rulers3D={rulers3D}
              selectedIds={selectedIds}
              onDragActiveChange={setSymbolDragActive}
            />
          )}
          <Ruler3DConstructionLayer
            active={active && editorStore.ruler3DConstructionMode}
          />
          <AmbientOcclusionOverlay
            floors={floors}
            wallHeight={ambientOcclusionWallHeight}
            hiddenFloorIds={cameraHiddenFloorIds}
          />
        </group>
      </BuiltInMaterialSurfaceMappingsContext.Provider>
    );
  },
);

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function hasPointerMissedGestureMoved(
  gesture: {
    clientX: number;
    clientY: number;
  },
  event: PointerEvent,
): boolean {
  return (
    Math.abs(event.clientX - gesture.clientX) >
      POINTER_MISSED_DRAG_THRESHOLD_PX ||
    Math.abs(event.clientY - gesture.clientY) > POINTER_MISSED_DRAG_THRESHOLD_PX
  );
}

function supportsAutomaticSymbolRotationGizmo(
  symbol: SymbolType,
  productAssetsEnabled: boolean,
): boolean {
  if (isCircleStairsType(symbol) || isRectStairsType(symbol)) return true;
  if (isShapePrimitiveSymbol(symbol)) return true;
  if (symbol.type !== "svg") return false;
  if (
    symbol.threeD?.userModel?.status === "ready" &&
    symbol.threeD.userModel.url
  ) {
    return true;
  }
  if (
    productAssetsEnabled &&
    symbol.threeD?.product?.asset?.status === "ready" &&
    symbol.threeD.product.asset.glbUrl
  ) {
    return true;
  }
  return Boolean(getPrimitiveSymbol3DModelKeyForSymbol(symbol));
}

export default FloorPlan3DScene;
